import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { NakedButton, SpinningLoader, Text } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../../constants';
import type { CustomProviderType } from '../../../../hooks/booking-site/use-get-appointment-types-providers';
import { useFetchAndStoreProviderAvailabilities } from '../../../../hooks/booking-site/use-get-calendar-availabilities';
import { useBookingSiteStore } from '../../../../stores/use-booking-site-store';
import { BOOKING_SITE_TRACKING_IDS } from '../../../../trackingIds/booking-site-tracking-ids';
import { BOOKING_SITE_HTTP_OPTIONS } from '../../../../utils';
import { endOfWeek } from '../../../../utils/date-helpers';

const WEEKDAYS = [
  i18next.t('Sunday', { ns: 'schedule' }),
  i18next.t('Monday', { ns: 'schedule' }),
  i18next.t('Tuesday', { ns: 'schedule' }),
  i18next.t('Wednesday', { ns: 'schedule' }),
  i18next.t('Thursday', { ns: 'schedule' }),
  i18next.t('Friday', { ns: 'schedule' }),
  i18next.t('Saturday', { ns: 'schedule' }),
];

interface Props {
  provider: CustomProviderType;
}

export const getSlotsGroupByDay = (slots: string[]) => {
  return slots.reduce<Record<string, string[]>>((acc, slot) => {
    const day = formatDate(slot, 'YYYY-MM-DD');
    if (!acc[day]) {
      acc[day] = [];
    }
    if (acc[day].indexOf(slot) === -1) {
      acc[day].push(slot ?? '');
    }
    return acc;
  }, {});
};

const getWeekWiseSlotCount = (slotsGroupByDay: Record<string, string[]>) =>
  Object.entries(slotsGroupByDay).reduce<Record<string, number>>(
    (acc, [date, slots]) => ({ ...acc, [dayjs(date).format('dddd')]: slots.length }),
    {}
  );

export const ProviderApptCount = ({ provider }: Props) => {
  const {
    availableProviders,
    isScheduleDefinitionsEnabled,
    selectedLocationId,
    setSelectedProvider,
    allProviderAvailabilities,
    selectedAppointmentType,
    selectedLocationTimeZone,
    setAllProviderAvailabilities,
    weekInView,
  } = useBookingSiteStore([
    'availableProviders',
    'isScheduleDefinitionsEnabled',
    'selectedLocationId',
    'setSelectedProvider',
    'allProviderAvailabilities',
    'selectedAppointmentType',
    'selectedLocationTimeZone',
    'setAllProviderAvailabilities',
    'weekInView',
  ]);

  const { isLoading } = useFetchAndStoreProviderAvailabilities({
    providerId: provider.id ?? '',
    providerCalendarId: provider.calendarId ?? '',
  });

  const { startDateTime, endDateTime } = useMemo(() => {
    const weekDate = dayjs(weekInView).tz(selectedLocationTimeZone);
    // The availability api requires the startDateTime to be at the start of the day (midnight). Lead time needs to be added to the current date and still be at the start of the day. IMPORTANT: Although we need to fetch the entire day of availabilities - we're displaying the slots which are either after the current time or the selectedAppointmentLeadTime.
    return {
      startDateTime: weekDate.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
      endDateTime: endOfWeek(weekInView, selectedLocationTimeZone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
    };
  }, [selectedLocationTimeZone, weekInView]);

  const query = SchedulerV3.Queries.useListBookableAppointmentSlotsQuery({
    request: {
      startDateTime: startDateTime,
      endDateTime: endDateTime,
      locationId: selectedLocationId,
      providerIds: availableProviders.map(({ id }) => id || ''),
      bookableAppointmentTypeId: selectedAppointmentType?.bookableAppointmentTypeId ?? '',
      slotDuration: selectedAppointmentType?.durationMinutes ?? 10,
    },
    options: { enabled: isScheduleDefinitionsEnabled && !!selectedLocationId },
    httpOptions: BOOKING_SITE_HTTP_OPTIONS,
    bypassScheduleV3FlagCheck: true,
  });

  const weekWiseBookableSlotCount = useMemo(() => {
    const slotDateTimes =
      query.data?.providerSlots
        ?.filter((p) => p.providerId === provider.id)
        .flatMap((p) => p.slots ?? [])
        .map((s) => (s.startDateTime ? formatDate(s.startDateTime, 'YYYY-MM-DDTHH:mm:ss') : ''))
        .filter(Boolean) ?? [];

    return getWeekWiseSlotCount(getSlotsGroupByDay(slotDateTimes));
  }, [query.data, provider.id]);

  useEffect(() => {
    if (query.isLoading) {
      setAllProviderAvailabilities(provider.id || '', []);
    } else {
      const providerAvailabilities =
        query.data?.providerSlots
          ?.filter((p) => p.providerId === provider.id)
          ?.flatMap(
            (i) =>
              i.slots
                ?.flatMap((s) => (s.startDateTime ? formatDate(s.startDateTime, 'YYYY-MM-DDTHH:mm:ss') : ''))
                .filter(Boolean) ?? []
          ) ?? [];
      setAllProviderAvailabilities(provider.id || '', providerAvailabilities);
    }
  }, [provider.id, query.data?.providerSlots, query.isLoading, setAllProviderAvailabilities]);

  const weekWiseSlotCount = useMemo(() => {
    const selectedProviderAvailabilities = (provider.id ? allProviderAvailabilities[provider.id] : []) ?? [];

    return getWeekWiseSlotCount(getSlotsGroupByDay(selectedProviderAvailabilities));
  }, [allProviderAvailabilities, provider.id]);

  return (
    <div css={containerStyle}>
      {query.isLoading || isLoading ? (
        <div css={{ width: '100%', alignContent: 'center' }}>
          <SpinningLoader size='small' />
        </div>
      ) : (
        <WeekWiseSlots
          slotsMap={isScheduleDefinitionsEnabled ? weekWiseBookableSlotCount : weekWiseSlotCount}
          onSelect={(day) => setSelectedProvider(provider, day)}
        />
      )}
    </div>
  );
};

export const AllProvidersApptCount = () => {
  const { setSelectedProvider, allProviderAvailabilities } = useBookingSiteStore([
    'setSelectedProvider',
    'allProviderAvailabilities',
  ]);

  const weekWiseSlotsMap = useMemo(() => {
    const allSlots = Object.values(allProviderAvailabilities).flat();
    return getWeekWiseSlotCount(getSlotsGroupByDay(allSlots));
  }, [allProviderAvailabilities]);

  return (
    <div css={containerStyle}>
      <WeekWiseSlots slotsMap={weekWiseSlotsMap} onSelect={(day) => setSelectedProvider('any', day)} />
    </div>
  );
};

const WeekWiseSlots = ({
  slotsMap,
  onSelect,
}: {
  slotsMap: Record<string, number>;
  onSelect: (day: string) => void;
}) => {
  const { t } = useTranslation('bookingSite');

  return WEEKDAYS.map((day) => {
    const count = slotsMap[day] ?? 0;
    return (
      <div key={day} css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
        <Text css={{ margin: 0 }}>{day.substring(0, 3)}</Text>
        <NakedButton
          css={buttonStyle(!!count)}
          disabled={!count}
          trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.weekDaySelect}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(day);
          }}
        >
          {count ? t('{{count}} appts', { count }) : t('No appts')}
        </NakedButton>
      </div>
    );
  });
};

const containerStyle = css({
  display: 'flex',
  position: 'relative',
  gap: theme.spacing(1),
  minHeight: 100,
  width: '100%',
  margin: theme.spacing(1.5, 1),
});

const buttonStyle = (hasCount: boolean) =>
  css({
    height: '100%',
    padding: theme.spacing(1),
    fontSize: theme.fontSize(16),
    borderRadius: theme.borderRadius.small,
    textAlign: 'left',
    border: `1px solid ${hasCount ? theme.colors.neutral30 : theme.colors.neutral20} !important`, // need to override disable button style
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: theme.colors.neutral10,
    },
  });
